import React, { useEffect, useState, Fragment } from 'react';
import Localize from 'react-intl-universal';
import { Link } from 'react-router-dom';

import { isEmpty } from 'lodash-es';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import NoContent from '@components/NoContentComponent/NoContent.jsx';
import RichTextEditor from '@components/RichTextEditor/RichTextEditor';
import { downloadTemplate } from '@pages/EventDetails/Util';

const StyledCancelButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: '700',
  color: theme.palette.primary.main
}));

const StyledListItemText = styled(ListItemText)(() => ({
  wordWrap: ' break-word',
  '.MuiTypography-root': {
    fontWeight: 'bold'
  }
}));

const StyledLink = styled(Link)(() => ({
  wordWrap: ' break-word'
}));

const Consents = ({ fetchConsents, consents = [], minHeight }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedConsentDescription, setSelectedConsentDescription] = useState(null);
  const [openConsentDialog, setOpenConsentDialog] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (isEmpty(consents)) {
        setIsLoading(true);
        await fetchConsents();
        setIsLoading(false);
      }
    };

    fetch();
  }, []);

  const handleOpenDescription = (description, fileURL, url, fileName) => {
    if (url) {
      window.open(url, '_blank');
    } else if (fileURL) {
      if (fileURL.startsWith('http')) {
        window.open(fileURL, '_blank');
      } else {
        downloadTemplate(fileName, fileURL);
      }
    } else {
      setSelectedConsentDescription(description);
      setOpenConsentDialog(true);
    }
  };

  const handleCloseDescription = () => {
    setOpenConsentDialog(false);
  };

  return (
    <Container sx={{ minHeight: minHeight }}>
      {isLoading && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '20vh'
          }}
        >
          <CircularProgress color='inherit' />
        </Box>
      )}
      {!isLoading && !isEmpty(consents) && (
        <>
          <Typography
            sx={{ mb: 1, mt: { xs: 1, sm: 'inherit' }, fontWeight: 'bold', textAlign: 'center' }}
            variant='h6'
            component='div'
          >
            {Localize.get('EventDetails.consentsList')}
          </Typography>

          <List sx={{ pl: { xs: 0, md: '10rem' }, pr: { xs: 0, md: '10rem' } }}>
            {consents?.map(
              ({ id, name, shortDescription, description, fileURL, url, fileName }) => (
                <Fragment key={id}>
                  <ListItem>
                    <Grid container spacing={2}>
                      <Grid item sm={9}>
                        <StyledListItemText primary={shortDescription || ''} />
                      </Grid>
                      <Grid item sm={3}>
                        <StyledLink
                          component='button'
                          type='button'
                          onClick={(e) => {
                            e.preventDefault();
                            handleOpenDescription(description || '', fileURL, url, fileName);
                          }}
                          style={{ pointerEvents: 'auto' }}
                        >
                          {name || ''}
                        </StyledLink>
                      </Grid>
                    </Grid>
                  </ListItem>
                  <Divider component='li' />
                </Fragment>
              )
            )}
          </List>
        </>
      )}
      {!isLoading && isEmpty(consents) && (
        <Box data-testid={'noConsentMessage'}>
          <NoContent infoType={Localize.get('EventDetails.noConsentsAvailable')} />
        </Box>
      )}
      <Dialog open={openConsentDialog} onClose={handleCloseDescription}>
        <DialogContent sx={{ minWidth: '40rem' }}>
          <RichTextEditor
            initialValue={selectedConsentDescription}
            emptyTextMessage={Localize.get('EventDetails.noTopics')}
            disabled
            isToolbarHidden
          />
        </DialogContent>
        <DialogActions>
          <StyledCancelButton onClick={handleCloseDescription}>
            {Localize.get('App.close')}
          </StyledCancelButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Consents;
